<template>
  <v-dialog
    transition="dialog-bottom-transition"
    :width="type === 'project' && $vuetify.display.mdAndUp ? '60%' : '100%'"
    v-model="dialogVisible"
  >
    <template v-slot:activator="{ props: activatorProps }">
      <ThemedActionButton
        @click="handleDialogActivation"
        v-bind="activatorProps"
        :text="buttonText"
        :buttonVariant="'outlined'"
      ></ThemedActionButton>
    </template>

    <template v-slot:default="{ isActive }">
      <v-card class="mb-4">
        <v-toolbar color="primary">
          <v-toolbar-title>{{ title }}</v-toolbar-title>
        </v-toolbar>

        <v-card-text>
          <v-form class="mt-5" v-model="formValid">
            <!-- Dynamic Fields -->
            <div v-for="(field, index) in fields" :key="index">
              <v-text-field
                v-if="field.type === 'text' && field.key !== 'client'"
                v-model="formData[field.key]"
                density="compact" 
                variant="outlined"
                :label="field.label"
                :rules="[
                  ...(field.required ? [rules.required] : []),
                  ...(field.rules || []),
                ]"
              />
              <!-- Combobox for Client -->
              <v-combobox
                v-else-if="field.key === 'client'"
                v-model="formData[field.key]"
                density="compact"
                variant="outlined"
                :items="field.options || []"
                :label="field.label"
                clearable
                :rules="[
                  ...(field.required ? [rules.required] : []),
                  ...(field.rules || []),
                ]"
              />
              <v-select
                v-else-if="field.type === 'select'"
                v-model="formData[field.key]"
                density="compact"
                variant="outlined"
                :items="field.options"
                :item-title="
                  field.options?.[0] &&
                  typeof field.options[0] === 'object'
                    ? 'title'
                    : undefined
                "
                :item-value="
                  field.options?.[0] &&
                  typeof field.options[0] === 'object'
                    ? 'value'
                    : undefined
                "
                :label="field.label"
                :rules="[
                  ...(field.required ? [rules.required] : []),
                  ...(field.rules || []),
                ]"
              />
              <v-autocomplete
                v-else-if="field.type === 'autocomplete-multiple'"
                v-model="formData[field.key]"
                density="compact"
                variant="outlined"
                :items="orderedOptionsByField[field.key] || field.options"
                item-title="title"
                item-value="value"
                :label="field.label"
                multiple
                chips
                closable-chips
                clearable
                :rules="[
                  ...(field.required ? [rules.atLeastOne] : []),
                  ...(field.rules || []),
                ]"
                @update:modelValue="
                  (val) => onMultiSelectChange(field.key, val, field.max)
                "
              />

              <!-- After Client: Parent project | Odoo ID | Supli Odoo ID -->
              <v-row
                v-if="type === 'project' && field.key === 'client'"
                dense
                class="mb-2"
              >
                <v-col cols="12" sm="4">
                  <v-autocomplete
                    v-model="formData.parentProjectId"
                    density="compact"
                    variant="outlined"
                    label="Parent project"
                    clearable
                    hide-details
                    :items="parentProjectOptions"
                    item-title="title"
                    item-value="value"
                    @update:modelValue="onParentProjectChange"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="formData.odooId"
                    density="compact"
                    variant="outlined"
                    label="Odoo ID"
                    hide-details
                    :disabled="hasParentProject"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="formData.odooIdSupli"
                    density="compact"
                    variant="outlined"
                    label="Supli Odoo ID"
                    hide-details
                  />
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="d-flex justify-space-between pl-10 pr-10">
          <v-btn text @click="resetFormData">CLOSE</v-btn>
          <v-btn color="primary" @click="handleSave" :disabled="!formValid"
            >SAVE</v-btn
          >
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>
</template>

<script>
import { ref, set, push, get } from "firebase/database";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
  sendSignInLinkToEmail,
} from "firebase/auth";
import { db, auth } from "@/firebaseConfig";
import { v4 as uuidv4 } from "uuid";
import {
  getParentProjectOptions,
  normalizeTeamLeadersForSave,
} from "@/utils/projectUtils";
import ThemedActionButton from "./ThemedActionButton.vue";
import { mapGetters } from "vuex";

export default {
  components: {
    ThemedActionButton,
  },
  props: {
    /**
     * Title to display in the dialog toolbar.
     * @type {string}
     * @required
     */
    title: { type: String, required: true },

    /**
     * Text for the dialog activator button.
     * @type {string}
     * @default 'ADD'
     */
    buttonText: { type: String, default: "ADD" },

    /**
     * Configuration array for dynamic form fields.
     * Each object contains field properties like key, type, label, and rules.
     * @type {Array<Object>}
     * @required
     */
    fields: { type: Array },

    /**
     * Options for dropdown fields.
     * @type {Array|null}
     * @default null
     */
    selectOptions: { type: Array, default: null },

    /**
     * Label for dropdown fields.
     * @type {string}
     * @default ''
     */
    selectLabel: { type: String, default: "" },

    /**
     * Key for mapping dropdown field value.
     * @type {string}
     * @default ''
     */
    selectKey: { type: String, default: "" },

    /**
     * Determines the save logic type, e.g., 'user' or 'project'.
     * @type {string}
     * @required
     */
    type: { type: String, required: true },
  },
  data() {
    return {
      /**
       * Boolean to control dialog visibility.
       * @type {boolean}
       */
      dialogVisible: false,

      /**
       * Boolean to track form validation status.
       * @type {boolean}
       */
      formValid: false,

      /**
       * Object to store dynamic form data.
       * @type {Object}
       */
      formData: {},

      /**
       * Validation rules for the form.
       * @type {Object}
       */
      rules: {
        required: (value) => !!value || "Required.",
        atLeastOne: (value) =>
          (Array.isArray(value) && value.length > 0) ||
          "There needs to be at least one team leader associated to a project.",
      },
    };
  },

  mounted() {
    this.resetFormData();
  },
  computed: {
    ...mapGetters(["getAllProjects", "getManagedProjects", "getWorkingProjects"]),
    hasParentProject() {
      return Boolean(this.formData.parentProjectId);
    },
    parentProjectOptions() {
      const source =
        (Array.isArray(this.getAllProjects) && this.getAllProjects.length
          ? this.getAllProjects
          : null) ||
        this.getManagedProjects ||
        this.getWorkingProjects ||
        [];
      return getParentProjectOptions(source);
    },
    /**
     * Cached ordered autocomplete options per field (selected first, then A–Z).
     * Avoids re-sorting from the template on every keystroke/render.
     */
    orderedOptionsByField() {
      const map = {};
      (this.fields || []).forEach((field) => {
        if (field.type !== "autocomplete-multiple") return;
        const selected = this.formData[field.key] || [];
        const options = [...(field.options || [])].sort((a, b) =>
          String(a.title || "").localeCompare(String(b.title || ""))
        );
        const selectedOpts = [];
        selected.forEach((uid) => {
          const opt = options.find((o) => o.value === uid);
          if (opt) selectedOpts.push(opt);
        });
        const rest = options.filter((o) => !selected.includes(o.value));
        map[field.key] = [...selectedOpts, ...rest];
      });
      return map;
    },
  },
  methods: {
    onMultiSelectChange(key, value, max = 2) {
      const next = Array.isArray(value) ? value.slice(0, max || 2) : [];
      this.formData[key] = next;
    },
    onParentProjectChange(parentId) {
      this.formData.parentProjectId = parentId || null;
      if (!parentId) return;
      const parent = (
        this.getAllProjects ||
        this.getManagedProjects ||
        this.getWorkingProjects ||
        []
      ).find((p) => p.projectId === parentId);
      this.formData.odooId = parent?.odooId || "";
    },
    /**
     * Handles the activation of the dialog.
     * - If the button text is "ADD NEW PROJECT", it fetches companies from Firebase.
     * - Otherwise, it simply sets the dialog visibility to `true`.
     *
     * @async
     * @returns {Promise<void>}
     */
    async handleDialogActivation() {
      if (this.buttonText === "ADD NEW PROJECT") {
        // Fetch companies when the button text is "ADD NEW PROJECT"
        await this.$store.dispatch("fetchCompanies");
      }

      // Set dialog visibility to true
      this.dialogVisible = true;
    },

    /**
     * Resets the dynamic form data to default values.
     */
    resetFormData() {
      this.dialogVisible = false;

      this.formData = this.fields.reduce((acc, field) => {
        if (field.key === "role") {
          acc[field.key] = "operator";
        } else if (field.type === "autocomplete-multiple") {
          acc[field.key] = [];
        } else if (field.key === "isActive") {
          acc[field.key] = true;
        } else {
          acc[field.key] = "";
        }
        return acc;
      }, {});

      if (this.type === "project") {
        this.formData.parentProjectId = null;
        this.formData.odooId = this.formData.odooId || "";
        this.formData.odooIdSupli = this.formData.odooIdSupli || "";
      }
    },

    /**
     * Handles save action based on the type of data being saved.
     * Calls `saveUser` or `saveProject` methods.
     */
    async handleSave() {
      if (this.type === "user") {
        await this.saveUser();
        this.resetFormData();
      } else if (this.type === "project") {
        await this.saveProject();
        this.resetFormData();
      } else {
        console.error("Unknown type:", this.type);
      }
      this.dialogVisible = false; // Close dialog
    },

    /**
     * Saves a new user invite to Firebase and sends a signup link via email.
     * - Stores the invitation details under `pendingInvites/{email}` in Firebase.
     * - Generates a temporary password for the user.
     * - Sends an email invitation with a signup link.
     *
     * @async
     * @returns {Promise<void>} Resolves when the invite is successfully sent or logs an error.
     */
    async saveUser() {
      try {
        const { inviteEmail, role } = this.formData;
        const temporaryPassword = uuidv4();

        //  Step 1: Save invite details using the email as key (or another unique identifier)
        await set(
          ref(db, `pendingInvites/${inviteEmail.replace(/\./g, "_")}`),
          {
            email: inviteEmail,
            role: role || "operator",
            userStatus: "pending",
            accountStatus: "inactive",
            tempPassword: temporaryPassword, // Store temp password for later use
          }
        );

        //  Step 2: Send Invite Email with Signup Link
        const actionCodeSettings = {
          url: `${window.location.origin}/signup?email=${encodeURIComponent(
            inviteEmail
          )}`,
          handleCodeInApp: true,
        };

        await sendSignInLinkToEmail(auth, inviteEmail, actionCodeSettings);
        alert(`Invitation sent to ${inviteEmail}.`);
      } catch (error) {
        console.error("Error sending invite:", error);
        alert(`Error sending invite: ${error.message}`);
      }
    },
    /**
     * Saves a new project to Firebase with the provided data.
     * @async
     */
    async saveProject() {
      try {
        const {
          name,
          description,
          client,
          teamLeaders,
          odooIdSupli,
          technicalSupport,
          isActive,
        } = this.formData;

        const leads = normalizeTeamLeadersForSave(teamLeaders);
        if (!leads.teamLeaders.length) {
          alert(
            "There needs to be at least one team leader associated to a project."
          );
          return;
        }

        const parentProjectId = this.formData.parentProjectId || null;
        let odooId = this.formData.odooId || "";
        if (parentProjectId) {
          const parent = (
            this.getAllProjects ||
            this.getManagedProjects ||
            this.getWorkingProjects ||
            []
          ).find((p) => p.projectId === parentProjectId);
          odooId = parent?.odooId || "";
        }

        const projectsRef = ref(db, `projects`);

        const newProject = {
          name,
          description,
          client,
          ...leads,
          odooId,
          odooIdSupli,
          technicalSupport,
          isActive: isActive !== false,
          separateOdooEntries: false,
          priority: "Normal",
          createdAt: new Date().toISOString(),
        };
        if (parentProjectId) {
          newProject.parentProjectId = parentProjectId;
        }

        // Save the project to Firebase
        await set(push(projectsRef), newProject);

        // Save the client to Firebase if it's new
        if (client) {
          const clientsRef = ref(db, "companies");
          const clientSnapshot = await get(clientsRef);

          // Normalize the client title (trim spaces and convert to lowercase)
          const normalizedClient = client.trim().toLowerCase();

          // Extract existing normalized titles
          const existingClients = clientSnapshot.exists()
            ? Object.values(clientSnapshot.val()).map((c) =>
                c.title.trim().toLowerCase()
              )
            : [];

          // Only push the client if it doesn't already exist
          if (!existingClients.includes(normalizedClient)) {
            await push(clientsRef, { title: client.trim() }); // Save the trimmed title
          }
        }
        alert(`Project "${name}" added successfully.`);
      } catch (error) {
        console.error("Error saving project:", error);
        alert("Error saving project.");
      }
    },
  },
};
</script>
